import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  Calendar,
  Filter,
  ChevronLeft,
  MoreVertical,
  FileText,
  Heart,
  Pill,
  Syringe,
  Activity,
  UserPlus
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { patientApi, getLocalizedValue } from '@/services/clinicApi';

const ClinicPatients = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const [newPatient, setNewPatient] = useState({
    first_name: { en: '', ar: '' },
    last_name: { en: '', ar: '' },
    phone_number: '',
    email: '',
    date_of_birth: '',
    gender: 'male',
    blood_type: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPatients();
  }, [isAuthenticated, navigate]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await patientApi.getPatients({ limit: 100 });
      const data = response.data?.data || [];
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل المرضى' : 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = async () => {
    try {
      await patientApi.createPatient(newPatient);
      toast.success(lang === 'ar' ? 'تم إضافة المريض بنجاح' : 'Patient added successfully');
      setIsAddDialogOpen(false);
      setNewPatient({
        first_name: { en: '', ar: '' },
        last_name: { en: '', ar: '' },
        phone_number: '',
        email: '',
        date_of_birth: '',
        gender: 'male',
        blood_type: '',
      });
      fetchPatients();
    } catch (error) {
      toast.error(lang === 'ar' ? 'فشل في إضافة المريض' : 'Failed to add patient');
    }
  };

  const filteredPatients = patients.filter((patient) => {
    const name = `${getLocalizedValue(patient.first_name, lang)} ${getLocalizedValue(patient.last_name, lang)}`.toLowerCase();
    const phone = patient.phone_number || '';
    return name.includes(searchQuery.toLowerCase()) || phone.includes(searchQuery);
  });

  const getGenderIcon = (gender) => {
    return gender === 'male' || gender === 'ذكر' ? '♂️' : '♀️';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-patients">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'إدارة المرضى' : 'Patient Management'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {filteredPatients.length} {lang === 'ar' ? 'مريض' : 'patients'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={lang === 'ar' ? 'بحث عن مريض...' : 'Search patients...'}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <UserPlus className="h-4 w-4 mr-2" />
                    {lang === 'ar' ? 'إضافة مريض' : 'Add Patient'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{lang === 'ar' ? 'إضافة مريض جديد' : 'Add New Patient'}</DialogTitle>
                    <DialogDescription>
                      {lang === 'ar' ? 'أدخل بيانات المريض' : 'Enter patient information'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{lang === 'ar' ? 'الاسم الأول' : 'First Name'}</Label>
                        <Input
                          value={newPatient.first_name[lang] || newPatient.first_name.en}
                          onChange={(e) => setNewPatient({
                            ...newPatient,
                            first_name: { ...newPatient.first_name, [lang]: e.target.value }
                          })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{lang === 'ar' ? 'اسم العائلة' : 'Last Name'}</Label>
                        <Input
                          value={newPatient.last_name[lang] || newPatient.last_name.en}
                          onChange={(e) => setNewPatient({
                            ...newPatient,
                            last_name: { ...newPatient.last_name, [lang]: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{lang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</Label>
                      <Input
                        value={newPatient.phone_number}
                        onChange={(e) => setNewPatient({ ...newPatient, phone_number: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input
                        type="email"
                        value={newPatient.email}
                        onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{lang === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth'}</Label>
                        <Input
                          type="date"
                          value={newPatient.date_of_birth}
                          onChange={(e) => setNewPatient({ ...newPatient, date_of_birth: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{lang === 'ar' ? 'الجنس' : 'Gender'}</Label>
                        <Select
                          value={newPatient.gender}
                          onValueChange={(value) => setNewPatient({ ...newPatient, gender: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">{lang === 'ar' ? 'ذكر' : 'Male'}</SelectItem>
                            <SelectItem value="female">{lang === 'ar' ? 'أنثى' : 'Female'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button onClick={handleAddPatient} className="bg-teal-600 hover:bg-teal-700">
                      {lang === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Patient Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredPatients.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {lang === 'ar' ? 'لا توجد نتائج' : 'No patients found'}
              </h3>
              <p className="text-slate-500 text-center max-w-md">
                {searchQuery 
                  ? (lang === 'ar' ? 'جرب بحث مختلف' : 'Try a different search')
                  : (lang === 'ar' ? 'أضف مريضك الأول' : 'Add your first patient')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPatients.map((patient) => {
              const name = `${getLocalizedValue(patient.first_name, lang) || ''} ${getLocalizedValue(patient.last_name, lang) || ''}`.trim();
              
              return (
                <Card 
                  key={patient.id} 
                  className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/clinic/patients/${patient.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={patient.media?.[0]?.url} />
                        <AvatarFallback className="bg-teal-100 text-teal-700">
                          {name.charAt(0) || 'P'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-slate-900 dark:text-white truncate">
                            {name || `Patient ${patient.id}`}
                          </h3>
                          <span className="text-lg">{getGenderIcon(patient.gender)}</span>
                        </div>
                        <div className="mt-2 space-y-1">
                          {patient.phone_number && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                              <Phone className="h-3 w-3" />
                              <span>{patient.phone_number}</span>
                            </div>
                          )}
                          {patient.blood_type && (
                            <Badge variant="secondary" className="text-xs">
                              {patient.blood_type}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">
                        {patient.total_appointments || 0} {lang === 'ar' ? 'زيارة' : 'visits'}
                      </span>
                      <Button variant="ghost" size="sm" onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clinic/patients/${patient.id}/records`);
                      }}>
                        <FileText className="h-4 w-4 mr-1" />
                        {lang === 'ar' ? 'السجلات' : 'Records'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicPatients;
